// Template placeholder validation — shared between email and doc pipelines.
//
// Two concerns:
// 1. Extract placeholders from a template body (Handlebars {{...}} or Docs <...>)
// 2. Classify missing placeholders as critical (block) or informational (proceed)
//    using the schema stored on the template config.

import type { PlaceholderSchema } from '@shared/types/common.ts';

// ── Extraction ──────────────────────────────────────────────────────────

const HANDLEBARS_HELPERS = new Set([
  // Built-ins
  'if', 'else', 'each', 'unless', 'with', 'log', 'this',
  // Date/number/value formatting
  'formatDate', 'formatTime', 'formatWeight', 'formatNumber',
  'formatPrice', 'formatPhone', 'productName',
  'getDayLocative', 'getDayName', 'formatDriverDetails',
  // Email structure
  'EMAIL_FOOTER', 'EMAIL_HEADER', 'EMAIL_FOOTER_WRAPPER',
  // File / recipient helpers
  'filterFiles', 'getContactEmails', 'selectApprovedFile',
]);

/**
 * Extract all unique placeholder paths from a Handlebars template body.
 * Captures ALL identifiers inside `{{...}}` expressions — the helper name
 * (first word) AND its arguments (e.g. `{{formatDate loadDate}}` yields
 * `loadDate`). Known helpers are filtered from the output. Returns full
 * paths (e.g. `transport.driverPhone`).
 */
export function extractHandlebarsPlaceholders(body: string): string[] {
  if (!body) return [];
  const found = new Set<string>();
  // Match each {{...}} expression (including {{#...}} and {{/...}})
  const exprRegex = /\{\{[#/]?\s*([^}]*)\}\}/g;
  let exprMatch: RegExpExecArray | null;
  while ((exprMatch = exprRegex.exec(body)) !== null) {
    // Strip string literal arguments so their content isn't scanned as identifiers
    const inner = exprMatch[1]
      .replace(/"(?:[^"\\]|\\.)*"/g, '')
      .replace(/'(?:[^'\\]|\\.)*'/g, '');
    // Extract identifier paths (supports dotted/bracket access like `a.b[0]`)
    const identRegex = /([a-zA-Z_][a-zA-Z0-9_.[\]]*)/g;
    let identMatch: RegExpExecArray | null;
    while ((identMatch = identRegex.exec(inner)) !== null) {
      const path = identMatch[1];
      const root = path.split(/[.[]/)[0];
      if (HANDLEBARS_HELPERS.has(root)) continue;
      found.add(path);
    }
  }
  return Array.from(found).sort();
}

// JavaScript reserved words + standard globals — excluded from resolver
// identifier extraction since they aren't data fields.
const JS_RESERVED_AND_GLOBALS = new Set([
  // keywords / operators
  'true', 'false', 'null', 'undefined', 'return', 'typeof', 'instanceof',
  'new', 'if', 'else', 'for', 'while', 'switch', 'case', 'break', 'continue',
  'function', 'var', 'let', 'const', 'this', 'super', 'class', 'extends',
  'in', 'of', 'async', 'await', 'yield', 'throw', 'try', 'catch', 'finally',
  'delete', 'void', 'do', 'default', 'with', 'use', 'strict',
  // standard globals
  'String', 'Number', 'Boolean', 'Array', 'Object', 'Math', 'Date', 'JSON',
  'Promise', 'Symbol', 'Map', 'Set', 'Error', 'console', 'parseInt',
  'parseFloat', 'isNaN', 'isFinite', 'encodeURIComponent', 'decodeURIComponent',
  'Infinity', 'NaN', 'RegExp',
  // injected into resolver scope (not a data field)
  'helpers',
]);

/**
 * Extract full access paths referenced in resolver code. Strips string
 * literals, comments, and object-literal keys first. Captures each full
 * chain starting from a root identifier — including property access and
 * bracket indices — e.g. `transports[0].transportCompany`. Optional
 * chaining (`?.`) is normalised to plain `.`.
 *
 * Filters out JS reserved words, standard globals, and `helpers`.
 */
export function extractResolverReferences(resolverCode: string | null | undefined): string[] {
  if (!resolverCode) return [];
  const sanitized = resolverCode
    .replace(/"(?:[^"\\]|\\.)*"/g, '""')
    .replace(/'(?:[^'\\]|\\.)*'/g, "''")
    .replace(/`(?:[^`\\]|\\.)*`/g, '``')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\/\/[^\n]*/g, '')
    // Strip object-literal keys (`key:` or `"key":`) — these are computed
    // outputs the resolver produces, not inputs it consumes.
    .replace(/\b([a-zA-Z_][a-zA-Z0-9_]*)(\s*:)/g, ' ')
    // Normalise optional chaining so we can use a single regex
    .replace(/\?\./g, '.');

  // Capture a root identifier + any chain of `.prop` / `[digits]` / `.digits`.
  const chainRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*|\[\d+\])*)/g;
  const found = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = chainRegex.exec(sanitized)) !== null) {
    const full = match[1];
    const root = full.split(/[.[]/)[0];
    if (JS_RESERVED_AND_GLOBALS.has(root)) continue;
    // Skip if this match started mid-chain (preceded by `.` means we're
    // the RHS of a dotted access that should already be captured as part
    // of the outer chain).
    const preceding = sanitized[match.index - 1];
    if (preceding === '.') continue;
    found.add(full);
  }
  return Array.from(found).sort();
}

/**
 * Extract placeholders from every placeholder-containing string in an email
 * template config: body (default + all language variants), subject (default
 * + all language variants), and root identifiers referenced by the resolver.
 * Returns the deduped, sorted union.
 */
export function extractAllEmailPlaceholders(config: {
  email_body_template?: string | null;
  email_body_templates?: Record<string, string> | null;
  email_subject_template?: string | null;
  email_subject_templates?: Record<string, string> | null;
  email_recipients_config?: { company?: string; purpose?: string } | null;
  email_language_source?: string | null;
  email_attachments_config?: Array<{ document_type?: string; select?: string }> | null;
  resolvers?: string | null;
}): string[] {
  const all = new Set<string>();
  const strings: (string | null | undefined)[] = [
    config.email_body_template,
    config.email_subject_template,
    ...Object.values(config.email_body_templates ?? {}),
    ...Object.values(config.email_subject_templates ?? {}),
  ];
  for (const s of strings) {
    if (!s) continue;
    for (const p of extractHandlebarsPlaceholders(s)) all.add(p);
  }
  for (const p of extractResolverReferences(config.resolvers)) all.add(p);

  // email_recipients_config emits a composite path describing what actually
  // needs to exist: a contact on {company} with purposes including {purpose}
  // and a non-empty email. Format: `{company}.contacts[{purpose}].email` —
  // classifyBySchema/EmailService.validate detect and run the direct check.
  const rc = config.email_recipients_config;
  if (rc?.company && rc?.purpose) {
    all.add(`${rc.company}.contacts[${rc.purpose}].email`);
  } else if (rc?.company) {
    all.add(rc.company);
  }

  // email_language_source names a data field whose country/language drives
  // which language variant is rendered (e.g. "loadPlaceCompany").
  if (config.email_language_source) all.add(config.email_language_source);

  // email_attachments_config: each entry requires a matching file in data.files.
  // Composite path: `attachments[{document_type}:{select}]`.
  for (const att of config.email_attachments_config ?? []) {
    if (att.document_type && att.select) {
      all.add(`attachments[${att.document_type}:${att.select}]`);
    }
  }

  return Array.from(all).sort();
}

/**
 * Extract all unique placeholder names from a Google Docs template body
 * using <placeholder> syntax. Used when syncing doc template schemas.
 */
export function extractDocPlaceholders(body: string): string[] {
  if (!body) return [];
  const refRegex = /<([a-zA-Z_][a-zA-Z0-9_.]*)>/g;
  const found = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = refRegex.exec(body)) !== null) {
    found.add(match[1]);
  }
  return Array.from(found).sort();
}

/**
 * Extract OUTPUT keys of a resolver's returned object literal.
 * For doc templates, these names are substituted into `<placeholder>` slots
 * in the Google Doc, so they're the natural placeholder list to validate.
 *
 * Scans the top-level object literal only (doesn't recurse into nested
 * objects). Skips string-literal keys and helpers-namespace sub-objects.
 */
export function extractResolverOutputKeys(resolverCode: string | null | undefined): string[] {
  if (!resolverCode) return [];
  // Strip string literals / comments first so they don't false-match.
  const sanitized = resolverCode
    .replace(/"(?:[^"\\]|\\.)*"/g, '""')
    .replace(/'(?:[^'\\]|\\.)*'/g, "''")
    .replace(/`(?:[^`\\]|\\.)*`/g, '``')
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\/\/[^\n]*/g, '');
  // Find `key:` patterns, but only at depth-1 (direct keys of the outer object).
  const keyRegex = /([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g;
  const found = new Set<string>();
  const depth = 0;
  let match: RegExpExecArray | null;
  // Track depth by walking the sanitized string once — simpler than a parser.
  // First pass: identify positions where depth == 1 (just inside the top `{`).
  const depthAt: number[] = new Array<number>(sanitized.length).fill(0);
  let d = 0;
  for (let i = 0; i < sanitized.length; i++) {
    const c = sanitized[i];
    if (c === '{') d++;
    else if (c === '}') d--;
    depthAt[i] = d;
  }
  while ((match = keyRegex.exec(sanitized)) !== null) {
    // Position of the `key:` — the identifier starts at index + 1 (past `{` or `,`).
    const keyStart = match.index + match[1].length;
    // The key is at depth 1 if the opening `{` was the outer one.
    if (depthAt[keyStart] !== 1) continue;
    const key = match[2];
    if (key === 'helpers') continue;
    found.add(key);
  }
  void depth;
  return Array.from(found).sort();
}

/**
 * Combine all placeholders needed to safely render a doc template:
 * - Resolver INPUT paths (data fields the resolver reads) — so the resolver
 *   itself doesn't silently fail and leave output fields empty.
 * - Resolver OUTPUT keys — what the doc body actually substitutes.
 * - Explicit body placeholders from a recent test run (Google Docs fetch)
 *   when available; these are authoritative.
 *
 * Either source alone is incomplete — unioning them gives admin the full
 * surface to classify.
 */
export function extractAllDocPlaceholders(config: {
  resolvers?: string | null;
  /** Placeholders pulled from the Google Doc during the last test run. */
  testRunDocPlaceholders?: string[] | null;
}): string[] {
  const all = new Set<string>();
  for (const p of extractResolverReferences(config.resolvers)) all.add(p);
  for (const p of extractResolverOutputKeys(config.resolvers)) all.add(p);
  for (const p of config.testRunDocPlaceholders ?? []) all.add(p);
  return Array.from(all).sort();
}

// ── Value lookup + emptiness ────────────────────────────────────────────

/**
 * Deep-lookup handling both dotted and bracket access:
 *   getNested(d, "transport.driverPhone")          → d.transport.driverPhone
 *   getNested(d, "transports[0].transportCompany") → d.transports[0].transportCompany
 */
function getNested(data: Record<string, unknown>, path: string): unknown {
  const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  return parts.reduce<unknown>(
    (obj, key) => (obj as Record<string, unknown> | null | undefined)?.[key],
    data,
  );
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

/**
 * Composite path format: `{company}.contacts[{purpose}].email`
 * Represents "{company} has a non-deleted contact with {purpose} in its
 * purposes list and a non-empty email". Emitted by extractAllEmailPlaceholders
 * from email_recipients_config. Match pattern, not a real data path.
 */
const CONTACT_PATH_RE = /^([a-zA-Z_][a-zA-Z0-9_.]*)\.contacts\[([a-zA-Z_][a-zA-Z0-9_]*)\]\.email$/;

/**
 * Composite path format: `attachments[{document_type}:{select}]`
 * Represents "data.files contains a file matching document_type and select
 * strategy". Emitted by extractAllEmailPlaceholders from email_attachments_config.
 */
const ATTACHMENT_PATH_RE = /^attachments\[([a-zA-Z_][a-zA-Z0-9_]*):([a-zA-Z_][a-zA-Z0-9_]*)\]$/;

interface Contact {
  email?: string | null;
  purposes?: string[] | null;
  is_deleted?: boolean;
  isDeleted?: boolean;
}

interface FileEntry {
  documentType?: string;
  document_type?: string;
  documentStatus?: string;
  document_status?: string;
  reviewStatus?: string;
  review_status?: string;
  isLatest?: boolean;
  is_latest?: boolean;
}

/**
 * Evaluate whether a field path is populated in the resolved data.
 * Handles two formats:
 * - Normal dotted paths (e.g. `transport.driverPhone`) — deep lookup
 * - Contact-purpose paths (e.g. `firstCarrier.contacts[transport_notification].email`)
 *   — checks for a non-deleted contact with matching purpose and non-empty email
 */
export function isFieldPopulated(path: string, data: Record<string, unknown>): boolean {
  const contactMatch = path.match(CONTACT_PATH_RE);
  if (contactMatch) {
    const [, company, purpose] = contactMatch;
    const companyData = getNested(data, company) as { contacts?: Contact[] } | null | undefined;
    const contacts = companyData?.contacts ?? [];
    return contacts.some(c =>
      !(c.is_deleted ?? c.isDeleted) &&
      !isEmpty(c.email) &&
      (c.purposes ?? []).includes(purpose),
    );
  }
  const attachmentMatch = path.match(ATTACHMENT_PATH_RE);
  if (attachmentMatch) {
    const [, docType, select] = attachmentMatch;
    const files = (data.files as FileEntry[] | null | undefined) ?? [];
    return files.some(f => {
      const dt = f.documentType ?? f.document_type;
      const ds = f.documentStatus ?? f.document_status;
      const rs = f.reviewStatus ?? f.review_status;
      const latest = f.isLatest ?? f.is_latest;
      if (dt !== docType) return false;
      if (!latest) return false;
      if (select === 'generated') return ds === 'generated';
      if (select === 'approved') return rs === 'approved';
      if (select === 'filter') return true; // any latest file of this type
      return false;
    });
  }
  const value = getNested(data, path);
  return !isEmpty(value);
}

// ── Classification ──────────────────────────────────────────────────────

export interface ClassifiedIssues {
  /** Missing fields that block the operation — surface as 'warning' for user fix. */
  critical: string[];
  /** Missing fields where the template can still render — log but proceed. */
  info: string[];
}

/**
 * Classify missing placeholders against a schema.
 *
 * - Fields explicitly `required` → critical when missing
 * - Fields explicitly `optional` → info when missing
 * - Fields with `required_if` → critical only when the trigger field is populated
 * - Fields not in schema:
 *     - schema is configured (has entries) → critical (drift — template
 *       added a placeholder the admin hasn't classified yet)
 *     - schema is empty (not yet configured) → info (backward-compat
 *       fallback for templates admin hasn't touched)
 *
 * `recipients` is always critical regardless of schema.
 */
export function classifyBySchema(
  missingFields: string[],
  schema: PlaceholderSchema | null | undefined,
  resolvedData: Record<string, unknown>,
): ClassifiedIssues {
  const result: ClassifiedIssues = { critical: [], info: [] };
  const fields = schema?.fields ?? {};
  const schemaConfigured = Object.keys(fields).length > 0;

  for (const path of missingFields) {
    // recipients is always critical — hardcoded invariant (no email = no send).
    if (path === 'recipients') {
      result.critical.push(path);
      continue;
    }

    const cfg = fields[path];

    if (!cfg) {
      // Not in schema — critical if schema is configured (drift), info otherwise
      if (schemaConfigured) result.critical.push(path);
      else result.info.push(path);
      continue;
    }

    if (cfg.status === 'required') {
      result.critical.push(path);
    } else if (cfg.status === 'optional') {
      result.info.push(path);
    } else if (cfg.status === 'required_if' && cfg.when) {
      const trigger = getNested(resolvedData, cfg.when);
      if (!isEmpty(trigger)) result.critical.push(path);
      // else: trigger not present → field is not required in this context, skip
    }
  }

  return result;
}

// ── Auto-extraction with schema merge ───────────────────────────────────

/**
 * Merge a newly-extracted placeholder list into an existing schema:
 * - Adds new placeholders as `required` (if you wrote it in the template,
 *   you meant it — admin flips the truly-optional ones)
 * - Removes placeholders no longer in the template
 * - Preserves existing classifications for still-present placeholders
 * - Updates `last_auto_extracted` to the new snapshot
 */
export function mergeSchema(
  current: PlaceholderSchema,
  extracted: string[],
): PlaceholderSchema {
  const extractedSet = new Set(extracted);
  const nextFields: Record<string, PlaceholderFieldConfig> = {};

  for (const path of extracted) {
    nextFields[path] = current.fields[path] ?? { status: 'required' };
  }

  // Drop fields no longer in the template
  for (const path of Object.keys(current.fields)) {
    if (extractedSet.has(path)) continue;
    // Keep required_if fields even if their path is dynamic (e.g. secondCarrier.x
    // that might not be in every extraction) — but for now, drop them. Admin
    // re-adds if needed.
  }

  return {
    fields: nextFields,
    last_auto_extracted: extracted,
  };
}

// Re-export the PlaceholderFieldConfig type used above
import type { PlaceholderFieldConfig } from '@shared/types/common.ts';
export type { PlaceholderFieldConfig };
